import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  resistorColors,
  multiplierColors,
  toleranceColors,
  temperatureCoefficient,
} from '../../mocks/resistance-colors';
import { wireMaterials, insulationTypes, standardWireSizes } from '../../mocks/wire-materials';

// ─── Types ───────────────────────────────────────────────────────────────────
type Tab = 'electrical' | 'resistance' | 'wire';
type CalcMode = 'current-power' | 'unit-convert' | 'kva-convert';
type LoadType = 'mixed' | 'capacitive' | 'inductive' | 'resistive';

interface ConversionResult {
  label: string;
  value: string | number;
}

// ─── Electrical Calculations ─────────────────────────────────────────────────
function ElectricalCalculations() {
  const [calcMode, setCalcMode] = useState<CalcMode>('current-power');
  const [calculationType, setCalculationType] = useState<'current' | 'power'>('current');
  const [phaseType, setPhaseType] = useState<'single' | 'three'>('single');
  const [inputValue, setInputValue] = useState('');
  const [voltage, setVoltage] = useState('220');
  const [powerFactor, setPowerFactor] = useState('0.8');
  const [result, setResult] = useState<
    { current?: number; power?: number; kw?: number; kva?: number; watts?: number } | null
  >(null);

  const [convertFrom, setConvertFrom] = useState<'hp' | 'w' | 'kw' | 'mw'>('hp');
  const [convertValue, setConvertValue] = useState('');
  const [conversionResults, setConversionResults] = useState<ConversionResult[]>([]);

  const [kvaMode, setKvaMode] = useState<
    | 'kva-to-current'
    | 'current-to-kva'
    | 'kva-to-hp'
    | 'hp-to-kva'
    | 'kva-to-kw'
    | 'kw-to-kva'
    | 'kva-to-mw'
  >('kva-to-current');
  const [kvaInput, setKvaInput] = useState('');
  const [kvaVoltage, setKvaVoltage] = useState('220');
  const [kvaPhase, setKvaPhase] = useState<'single' | 'three'>('single');
  const [kvaPf, setKvaPf] = useState('0.8');
  const [kvaResults, setKvaResults] = useState<ConversionResult[]>([]);
  const [showError, setShowError] = useState(false);

  const calculateCurrent = () => {
    const power = parseFloat(inputValue);
    const v = parseFloat(voltage);
    const pf = parseFloat(powerFactor);
    if (isNaN(power) || isNaN(v) || isNaN(pf)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const watts = power * 746;
    const kw = watts / 1000;
    const current =
      phaseType === 'single'
        ? (power * 746) / (v * pf)
        : (power * 746) / (Math.sqrt(3) * v * pf);
    const kva =
      phaseType === 'single' ? (v * current) / 1000 : (Math.sqrt(3) * v * current) / 1000;
    setResult({
      current: parseFloat(current.toFixed(2)),
      power,
      kw: parseFloat(kw.toFixed(2)),
      kva: parseFloat(kva.toFixed(2)),
      watts: parseFloat(watts.toFixed(2)),
    });
  };

  const calculatePower = () => {
    const current = parseFloat(inputValue);
    const v = parseFloat(voltage);
    const pf = parseFloat(powerFactor);
    if (isNaN(current) || isNaN(v) || isNaN(pf)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const power =
      phaseType === 'single'
        ? (v * current * pf) / 746
        : (Math.sqrt(3) * v * current * pf) / 746;
    const kva =
      phaseType === 'single' ? (v * current) / 1000 : (Math.sqrt(3) * v * current) / 1000;
    const kw = (power * 746) / 1000;
    const watts = power * 746;
    setResult({
      power: parseFloat(power.toFixed(2)),
      current,
      kw: parseFloat(kw.toFixed(2)),
      kva: parseFloat(kva.toFixed(2)),
      watts: parseFloat(watts.toFixed(2)),
    });
  };

  const handleUnitConvert = () => {
    const val = parseFloat(convertValue);
    if (isNaN(val)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    let watts = 0;
    if (convertFrom === 'hp') watts = val * 746;
    else if (convertFrom === 'w') watts = val;
    else if (convertFrom === 'kw') watts = val * 1000;
    else watts = val * 1000000;

    const results: ConversionResult[] = [];
    if (convertFrom !== 'hp')
      results.push({ label: 'القدرة الحصانية (HP)', value: `${(watts / 746).toFixed(4)} HP` });
    if (convertFrom !== 'w') results.push({ label: 'واط (W)', value: `${watts.toFixed(2)} W` });
    if (convertFrom !== 'kw')
      results.push({ label: 'كيلوواط (kW)', value: `${(watts / 1000).toFixed(4)} kW` });
    if (convertFrom !== 'mw')
      results.push({ label: 'ميغاواط (MW)', value: `${(watts / 1000000).toFixed(6)} MW` });
    setConversionResults(results);
  };

  const handleKvaConvert = () => {
    const val = parseFloat(kvaInput);
    const v = parseFloat(kvaVoltage);
    const pf = parseFloat(kvaPf);
    if (isNaN(val)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const results: ConversionResult[] = [];
    switch (kvaMode) {
      case 'kva-to-current': {
        const current =
          kvaPhase === 'single' ? (val * 1000) / v : (val * 1000) / (Math.sqrt(3) * v);
        results.push({ label: 'التيار (أمبير)', value: `${current.toFixed(2)} A` });
        results.push({ label: 'القدرة الفعلية (kW)', value: `${(val * pf).toFixed(2)} kW` });
        results.push({
          label: 'القدرة الحصانية (HP)',
          value: `${((val * pf * 1000) / 746).toFixed(2)} HP`,
        });
        results.push({ label: 'واط (W)', value: `${(val * pf * 1000).toFixed(2)} W` });
        results.push({ label: 'معامل القدرة', value: pf });
        results.push({ label: 'الجهد', value: `${v} V` });
        break;
      }
      case 'current-to-kva': {
        const kva = kvaPhase === 'single' ? (val * v) / 1000 : (Math.sqrt(3) * val * v) / 1000;
        results.push({ label: 'KVA', value: `${kva.toFixed(2)} KVA` });
        results.push({ label: 'القدرة الفعلية (kW)', value: `${(kva * pf).toFixed(2)} kW` });
        results.push({
          label: 'القدرة الحصانية (HP)',
          value: `${((kva * pf * 1000) / 746).toFixed(2)} HP`,
        });
        results.push({ label: 'واط (W)', value: `${(kva * pf * 1000).toFixed(2)} W` });
        break;
      }
      case 'kva-to-hp': {
        const kw = val * pf;
        results.push({
          label: 'القدرة الحصانية (HP)',
          value: `${((kw * 1000) / 746).toFixed(2)} HP`,
        });
        results.push({ label: 'القدرة الفعلية (kW)', value: `${kw.toFixed(2)} kW` });
        results.push({ label: 'واط (W)', value: `${(kw * 1000).toFixed(2)} W` });
        break;
      }
      case 'hp-to-kva': {
        const watts = val * 746;
        const kw = watts / 1000;
        const kva = kw / pf;
        const current =
          kvaPhase === 'single' ? (kva * 1000) / v : (kva * 1000) / (Math.sqrt(3) * v);
        results.push({ label: 'KVA', value: `${kva.toFixed(2)} KVA` });
        results.push({ label: 'القدرة الفعلية (kW)', value: `${kw.toFixed(2)} kW` });
        results.push({ label: 'التيار (أمبير)', value: `${current.toFixed(2)} A` });
        break;
      }
      case 'kva-to-kw': {
        const kw = val * pf;
        results.push({ label: 'كيلوواط (kW)', value: `${kw.toFixed(2)} kW` });
        results.push({ label: 'واط (W)', value: `${(kw * 1000).toFixed(2)} W` });
        results.push({
          label: 'القدرة الحصانية (HP)',
          value: `${((kw * 1000) / 746).toFixed(2)} HP`,
        });
        break;
      }
      case 'kw-to-kva': {
        const kva = val / pf;
        const current =
          kvaPhase === 'single' ? (kva * 1000) / v : (kva * 1000) / (Math.sqrt(3) * v);
        results.push({ label: 'KVA', value: `${kva.toFixed(2)} KVA` });
        results.push({ label: 'التيار (أمبير)', value: `${current.toFixed(2)} A` });
        results.push({
          label: 'القدرة الحصانية (HP)',
          value: `${((val * 1000) / 746).toFixed(2)} HP`,
        });
        break;
      }
      case 'kva-to-mw': {
        const kw = val * pf;
        results.push({ label: 'ميغاواط (MW)', value: `${(kw / 1000).toFixed(4)} MW` });
        results.push({ label: 'كيلوواط (kW)', value: `${kw.toFixed(2)} kW` });
        results.push({ label: 'واط (W)', value: `${(kw * 1000).toFixed(2)} W` });
        break;
      }
      default:
        break;
    }
    setKvaResults(results);
  };

  const kvaOptions = [
    { value: 'kva-to-current', label: 'KVA → تيار' },
    { value: 'current-to-kva', label: 'تيار → KVA' },
    { value: 'kva-to-hp', label: 'KVA → حصان' },
    { value: 'hp-to-kva', label: 'حصان → KVA' },
    { value: 'kva-to-kw', label: 'KVA → kW' },
    { value: 'kw-to-kva', label: 'kW → KVA' },
    { value: 'kva-to-mw', label: 'KVA → MW' },
  ];

  const unitOptions = [
    { value: 'hp', label: 'حصان (HP)' },
    { value: 'w', label: 'واط (W)' },
    { value: 'kw', label: 'كيلوواط (kW)' },
    { value: 'mw', label: 'ميغاواط (MW)' },
  ];

  const getKvaInputLabel = () => {
    const map: Record<string, string> = {
      'kva-to-current': 'KVA',
      'current-to-kva': 'التيار (أمبير)',
      'kva-to-hp': 'KVA',
      'hp-to-kva': 'القدرة الحصانية (HP)',
      'kva-to-kw': 'KVA',
      'kw-to-kva': 'كيلوواط (kW)',
      'kva-to-mw': 'KVA',
    };
    return map[kvaMode] || 'القيمة';
  };

  return (
    <div className="w-full space-y-4">
      {showError && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          <span className="text-sm">الرجاء إدخال قيم صحيحة</span>
          <button onClick={() => setShowError(false)} className="mr-2 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        {(['current-power', 'unit-convert', 'kva-convert'] as CalcMode[]).map((mode) => {
          const labels: Record<CalcMode, { icon: string; label: string }> = {
            'current-power': { icon: 'ri-flashlight-line', label: 'تيار وقدرة' },
            'unit-convert': { icon: 'ri-exchange-line', label: 'تحويل الوحدات' },
            'kva-convert': { icon: 'ri-swap-line', label: 'تحويل KVA' },
          };
          return (
            <button
              key={mode}
              onClick={() => setCalcMode(mode)}
              className={`py-2.5 px-2 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap cursor-pointer ${
                calcMode === mode
                  ? 'bg-[#F5A623] text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
              }`}
            >
              <i className={`${labels[mode].icon} ml-1`}></i>
              {labels[mode].label}
            </button>
          );
        })}
      </div>

      {calcMode === 'current-power' && (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
          <div className="flex gap-2">
            {(['current', 'power'] as const).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setCalculationType(t);
                  setResult(null);
                }}
                className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                  calculationType === t
                    ? 'bg-[#F5A623] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t === 'current' ? 'حساب التيار' : 'حساب القدرة'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {(['single', 'three'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPhaseType(p)}
                className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                  phaseType === p
                    ? 'bg-[#3D3D3D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p === 'single' ? 'Single Phase' : 'Three Phase'}
              </button>
            ))}
          </div>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] focus:border-transparent text-sm"
            placeholder={calculationType === 'current' ? 'أدخل القدرة (حصان)' : 'أدخل التيار (أمبير)'}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              value={voltage}
              onChange={(e) => setVoltage(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] focus:border-transparent text-sm"
              placeholder="الجهد (فولت)"
            />
            <input
              type="number"
              step="0.1"
              value={powerFactor}
              onChange={(e) => setPowerFactor(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] focus:border-transparent text-sm"
              placeholder="معامل القدرة"
            />
          </div>
          <button
            onClick={calculationType === 'current' ? calculateCurrent : calculatePower}
            className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-bold hover:bg-[#E09612] transition-colors whitespace-nowrap cursor-pointer"
          >
            احسب
          </button>
          {result && (
            <div className="bg-gradient-to-r from-[#F5A623]/10 to-[#3D3D3D]/10 rounded-lg p-4 border-r-4 border-[#F5A623]">
              <h3 className="font-bold text-[#3D3D3D] mb-3">التفاصيل الكاملة:</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'التيار', value: `${result.current} أمبير` },
                  { label: 'القدرة الحصانية', value: `${result.power} HP` },
                  { label: 'كيلوواط', value: `${result.kw} kW` },
                  { label: 'القدرة الظاهرية', value: `${result.kva} KVA` },
                  { label: 'واط', value: `${result.watts} W` },
                  { label: 'الجهد', value: `${voltage} V` },
                ].map((item) => (
                  <div key={item.label} className="bg-white/70 rounded-lg p-3">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="font-bold text-[#F5A623]">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {calcMode === 'unit-convert' && (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
          <select
            value={convertFrom}
            onChange={(e) => {
              setConvertFrom(e.target.value as typeof convertFrom);
              setConversionResults([]);
            }}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm cursor-pointer"
          >
            {unitOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={convertValue}
            onChange={(e) => setConvertValue(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
            placeholder="أدخل القيمة"
          />
          <button
            onClick={handleUnitConvert}
            className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-bold hover:bg-[#E09612] cursor-pointer whitespace-nowrap"
          >
            حوّل
          </button>
          {conversionResults.length > 0 && (
            <div className="bg-gradient-to-r from-[#F5A623]/10 to-[#3D3D3D]/10 rounded-lg p-4 border-r-4 border-[#F5A623] space-y-2">
              {conversionResults.map((r, i) => (
                <div key={i} className="flex justify-between bg-white/60 rounded-lg px-4 py-3">
                  <span className="text-sm text-gray-600">{r.label}</span>
                  <span className="font-bold text-[#F5A623]">{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {calcMode === 'kva-convert' && (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
            {kvaOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setKvaMode(opt.value as typeof kvaMode);
                  setKvaResults([]);
                }}
                className={`py-2 px-2 rounded-lg font-medium text-xs whitespace-nowrap cursor-pointer ${
                  kvaMode === opt.value
                    ? 'bg-[#F5A623] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <input
            type="number"
            value={kvaInput}
            onChange={(e) => setKvaInput(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
            placeholder={getKvaInputLabel()}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              value={kvaVoltage}
              onChange={(e) => setKvaVoltage(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="الجهد (فولت)"
            />
            <input
              type="number"
              step="0.1"
              value={kvaPf}
              onChange={(e) => setKvaPf(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="معامل القدرة"
            />
          </div>
          <div className="flex gap-2">
            {(['single', 'three'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setKvaPhase(p)}
                className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                  kvaPhase === p
                    ? 'bg-[#3D3D3D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p === 'single' ? 'Single Phase' : 'Three Phase'}
              </button>
            ))}
          </div>
          <button
            onClick={handleKvaConvert}
            className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-bold hover:bg-[#E09612] cursor-pointer whitespace-nowrap"
          >
            حوّل
          </button>
          {kvaResults.length > 0 && (
            <div className="bg-gradient-to-r from-[#F5A623]/10 to-[#3D3D3D]/10 rounded-lg p-4 border-r-4 border-[#F5A623] space-y-2">
              {kvaResults.map((r, i) => (
                <div
                  key={i}
                  className="flex justify-between bg-white/60 rounded-lg px-4 py-3 border-r-2 border-[#F5A623]/30"
                >
                  <span className="text-sm text-gray-600">{r.label}</span>
                  <span className="font-bold text-[#F5A623]">{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Resistance Calculator ────────────────────────────────────────────────────
function ResistanceCalculator() {
  const [bandCount, setBandCount] = useState<4 | 5 | 6>(4);
  const [band1, setBand1] = useState(0);
  const [band2, setBand2] = useState(0);
  const [band3, setBand3] = useState(0);
  const [multiplier, setMultiplier] = useState(0);
  const [tolerance, setTolerance] = useState(0);
  const [tempCoeff, setTempCoeff] = useState(0);

  const calculateResistance = () => {
    if (bandCount === 4) return (band1 * 10 + band2) * multiplierColors[multiplier].value;
    return (band1 * 100 + band2 * 10 + band3) * multiplierColors[multiplier].value;
  };

  const formatResistance = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} MΩ`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(2)} kΩ`;
    return `${value.toFixed(2)} Ω`;
  };

  const resistance = calculateResistance();
  const toleranceValue = toleranceColors[tolerance]?.value ?? 0;
  const minResistance = resistance * (1 - toleranceValue / 100);
  const maxResistance = resistance * (1 + toleranceValue / 100);

  return (
    <div className="w-full space-y-4">
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
        <div className="flex gap-2">
          {[4, 5, 6].map((count) => (
            <button
              key={count}
              onClick={() => setBandCount(count as 4 | 5 | 6)}
              className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                bandCount === count ? 'bg-[#F5A623] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {count} أشرطة
            </button>
          ))}
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-48 h-16 bg-[#D4A574] rounded-full relative flex items-center justify-center gap-1">
              <div className="absolute right-4 w-6 h-6 bg-gray-400 rounded-full"></div>
              <div className="flex gap-1">
                {[resistorColors[band1], resistorColors[band2]].map((c, i) => (
                  <div key={i} className="w-4 h-12" style={{ backgroundColor: c.color, border: '1px solid #666' }}></div>
                ))}
                {bandCount >= 5 && (
                  <div className="w-4 h-12" style={{ backgroundColor: resistorColors[band3].color, border: '1px solid #666' }}></div>
                )}
                <div className="w-1"></div>
                <div className="w-4 h-12" style={{ backgroundColor: multiplierColors[multiplier].color, border: '1px solid #666' }}></div>
                <div className="w-2"></div>
                <div className="w-4 h-12" style={{ backgroundColor: toleranceColors[tolerance].color, border: '1px solid #666' }}></div>
                {bandCount === 6 && (
                  <>
                    <div className="w-2"></div>
                    <div className="w-4 h-12" style={{ backgroundColor: temperatureCoefficient[tempCoeff].color, border: '1px solid #666' }}></div>
                  </>
                )}
              </div>
              <div className="absolute left-4 w-6 h-6 bg-gray-400 rounded-full"></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'الشريط الأول', value: band1, setter: setBand1, options: resistorColors },
              { label: 'الشريط الثاني', value: band2, setter: setBand2, options: resistorColors },
            ].map((item) => (
              <div key={item.label}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{item.label}</label>
                <select
                  value={item.value}
                  onChange={(e) => item.setter(parseInt(e.target.value))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-xs cursor-pointer"
                >
                  {item.options.map((c, i) => (
                    <option key={i} value={i}>
                      {c.name} ({c.value})
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {bandCount >= 5 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">الشريط الثالث</label>
                <select
                  value={band3}
                  onChange={(e) => setBand3(parseInt(e.target.value))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-xs cursor-pointer"
                >
                  {resistorColors.map((c, i) => (
                    <option key={i} value={i}>
                      {c.name} ({c.value})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">المضاعف</label>
              <select
                value={multiplier}
                onChange={(e) => setMultiplier(parseInt(e.target.value))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-xs cursor-pointer"
              >
                {multiplierColors.map((c, i) => (
                  <option key={i} value={i}>
                    {c.name} (×{c.value})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">التفاوت</label>
              <select
                value={tolerance}
                onChange={(e) => setTolerance(parseInt(e.target.value))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-xs cursor-pointer"
              >
                {toleranceColors.map((c, i) => (
                  <option key={i} value={i}>
                    {c.name} (±{c.value}%)
                  </option>
                ))}
              </select>
            </div>
            {bandCount === 6 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">معامل الحرارة</label>
                <select
                  value={tempCoeff}
                  onChange={(e) => setTempCoeff(parseInt(e.target.value))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-xs cursor-pointer"
                >
                  {temperatureCoefficient.map((c, i) => (
                    <option key={i} value={i}>
                      {c.name} ({c.value} ppm/K)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#F5A623]/10 to-[#3D3D3D]/10 rounded-lg p-4 border-r-4 border-[#F5A623]">
          <div className="bg-white/70 rounded-lg p-4 border-r-2 border-[#F5A623] mb-3">
            <p className="text-xs text-gray-500 mb-1">قيمة المقاومة</p>
            <p className="text-3xl font-bold text-[#F5A623]">{formatResistance(resistance)}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500">التفاوت</p>
              <p className="text-sm font-bold text-[#3D3D3D]">±{toleranceValue}%</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500">الأدنى</p>
              <p className="text-sm font-bold text-[#3D3D3D]">{formatResistance(minResistance)}</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500">الأقصى</p>
              <p className="text-sm font-bold text-[#3D3D3D]">{formatResistance(maxResistance)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Wire Size Calculator ─────────────────────────────────────────────────────
function WireSizeCalculator() {
  const loadTypeOptions = [
    { value: 'mixed' as LoadType, label: 'مختلط' },
    { value: 'capacitive' as LoadType, label: 'سعوي' },
    { value: 'inductive' as LoadType, label: 'حثي' },
    { value: 'resistive' as LoadType, label: 'مقاومة' },
  ];

  const [distanceValue, setDistanceValue] = useState('');
  const [distanceUnit, setDistanceUnit] = useState<'m' | 'cm' | 'yard'>('m');
  const [inputType, setInputType] = useState<'current' | 'power'>('current');
  const [inputValue, setInputValue] = useState('');
  const [powerUnit, setPowerUnit] = useState<'hp' | 'kw' | 'w'>('hp');
  const [temperature, setTemperature] = useState('30');
  const [phaseType, setPhaseType] = useState<'single' | 'three'>('single');
  const [voltageDrop, setVoltageDrop] = useState('3');
  const [powerFactor, setPowerFactor] = useState('0.8');
  const [loadType, setLoadType] = useState<LoadType>('mixed');
  const [wireMaterial, setWireMaterial] = useState('copper');
  const [insulationType, setInsulationType] = useState('pvc');
  const [voltage, setVoltage] = useState('220');
  const [installationType, setInstallationType] = useState<'above' | 'underground'>('above');
  const [result, setResult] = useState<{
    wireSize: number;
    actualVoltageDrop: number;
    voltageDropPercent: number;
    resistance: number;
    powerLoss: number;
    calculatedCurrent: number;
    recommendation: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const convertToMeters = (value: number, unit: string) => {
    if (unit === 'cm') return value / 100;
    if (unit === 'yard') return value * 0.9144;
    return value;
  };

  const convertPowerToWatts = (value: number) => {
    if (powerUnit === 'hp') return value * 746;
    if (powerUnit === 'kw') return value * 1000;
    return value;
  };

  const handleLoadTypeChange = (type: LoadType) => {
    setLoadType(type);
    if (type === 'resistive') setPowerFactor('1');
    else if (type === 'inductive') setPowerFactor('0.8');
    else if (type === 'capacitive') setPowerFactor('0.95');
    else setPowerFactor('0.85');
  };

  const calculateWireSize = () => {
    setErrorMsg(null);
    const distance = convertToMeters(parseFloat(distanceValue), distanceUnit);
    const temp = parseFloat(temperature);
    const drop = parseFloat(voltageDrop);
    const pf = parseFloat(powerFactor);
    const v = parseFloat(voltage);
    if ([distance, temp, drop, pf, v].some(isNaN)) {
      setErrorMsg('يرجى إدخال قيم صحيحة لجميع الحقول.');
      return;
    }
    let current = 0;
    if (inputType === 'current') {
      current = parseFloat(inputValue);
      if (isNaN(current) || current <= 0) {
        setErrorMsg('يرجى إدخال قيمة تيار صحيحة.');
        return;
      }
    } else {
      const powerValue = parseFloat(inputValue);
      if (isNaN(powerValue) || powerValue <= 0) {
        setErrorMsg('يرجى إدخال قيمة قدرة صحيحة.');
        return;
      }
      const powerInWatts = convertPowerToWatts(powerValue);
      current =
        phaseType === 'single' ? powerInWatts / (v * pf) : powerInWatts / (Math.sqrt(3) * v * pf);
    }
    const material = wireMaterials.find((m) => m.value === wireMaterial);
    if (!material) {
      setErrorMsg('المادة المحددة غير موجودة.');
      return;
    }
    const effectiveTemp = installationType === 'underground' ? temp + 5 : temp;
    const resistivity = material.resistivity * (1 + 0.004 * (effectiveTemp - 20));
    const installationFactor = installationType === 'underground' ? 1.25 : 1.0;
    const maxVoltageDrop = v * (drop / 100);
    const requiredArea =
      (phaseType === 'single'
        ? (2 * resistivity * current * distance) / maxVoltageDrop
        : (Math.sqrt(3) * resistivity * current * distance) / maxVoltageDrop) * installationFactor;
    const recommendedSize =
      standardWireSizes.find((s) => s >= requiredArea) || standardWireSizes[standardWireSizes.length - 1];
    const actualDrop =
      phaseType === 'single'
        ? (2 * resistivity * current * distance) / recommendedSize
        : (Math.sqrt(3) * resistivity * current * distance) / recommendedSize;
    const actualDropPercent = (actualDrop / v) * 100;
    const resistance = (resistivity * distance) / recommendedSize;
    const powerLoss = current * current * resistance * (phaseType === 'single' ? 2 : 3);
    let recommendation = '';
    if (actualDropPercent > drop) recommendation = 'هبوط الجهد الفعلي أعلى من المسموح، يُنصح باستخدام مقطع أكبر.';
    else if (actualDropPercent < drop * 0.5) recommendation = 'المقطع المختار يوفر هامش أمان جيد.';
    setResult({
      wireSize: recommendedSize,
      actualVoltageDrop: parseFloat(actualDrop.toFixed(2)),
      voltageDropPercent: parseFloat(actualDropPercent.toFixed(2)),
      resistance: parseFloat(resistance.toFixed(4)),
      powerLoss: parseFloat(powerLoss.toFixed(2)),
      calculatedCurrent: parseFloat(current.toFixed(2)),
      recommendation,
    });
  };

  return (
    <div className="w-full space-y-4">
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 space-y-4">
        <div className="flex gap-2">
          <input
            type="number"
            value={distanceValue}
            onChange={(e) => setDistanceValue(e.target.value)}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
            placeholder="المسافة"
          />
          <select
            value={distanceUnit}
            onChange={(e) => setDistanceUnit(e.target.value as 'm' | 'cm' | 'yard')}
            className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm cursor-pointer"
          >
            <option value="m">متر</option>
            <option value="cm">سم</option>
            <option value="yard">ياردة</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            <i className="ri-map-pin-line ml-1 text-[#F5A623]"></i>
            نوع التمديد
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setInstallationType('above')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer transition-all ${
                installationType === 'above'
                  ? 'bg-[#F5A623] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-building-line"></i>
              فوق الأرض
            </button>
            <button
              onClick={() => setInstallationType('underground')}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer transition-all ${
                installationType === 'underground'
                  ? 'bg-[#3D3D3D] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-arrow-down-line"></i>
              تحت الأرض
            </button>
          </div>
          {installationType === 'underground' && (
            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <i className="ri-information-line"></i>
              التمديد تحت الأرض يتطلب مقطع أكبر بسبب ارتفاع درجة الحرارة
            </p>
          )}
        </div>

        <div className="flex gap-2">
          {(['current', 'power'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setInputType(t)}
              className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                inputType === t ? 'bg-[#F5A623] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t === 'current' ? 'التيار' : 'القدرة'}
            </button>
          ))}
        </div>

        {inputType === 'current' ? (
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
            placeholder="التيار (أمبير)"
          />
        ) : (
          <div className="flex gap-2">
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="القدرة"
            />
            <select
              value={powerUnit}
              onChange={(e) => setPowerUnit(e.target.value as 'hp' | 'kw' | 'w')}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm cursor-pointer"
            >
              <option value="hp">حصان</option>
              <option value="kw">كيلوواط</option>
              <option value="w">واط</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">نوع الحمل</label>
          <div className="grid grid-cols-4 gap-1.5">
            {loadTypeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleLoadTypeChange(opt.value)}
                className={`py-2 rounded-lg font-medium text-xs whitespace-nowrap cursor-pointer ${
                  loadType === opt.value ? 'bg-[#F5A623] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              <i className="ri-temp-hot-line ml-1 text-[#F5A623]"></i>
              درجة الحرارة (°C)
              <span className="text-gray-400 font-normal mr-1">— تؤثر على مقاومة الموصل</span>
            </label>
            <input
              type="number"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="درجة الحرارة (°C)"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">الجهد (فولت)</label>
            <input
              type="number"
              value={voltage}
              onChange={(e) => setVoltage(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="الجهد (فولت)"
            />
          </div>
        </div>

        <div className="flex gap-2">
          {(['single', 'three'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPhaseType(p)}
              className={`flex-1 py-2 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer ${
                phaseType === p ? 'bg-[#3D3D3D] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p === 'single' ? 'Single Phase' : 'Three Phase'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              <i className="ri-arrow-down-line ml-1 text-[#F5A623]"></i>
              هبوط الجهد (%)
              <span className="text-gray-400 font-normal mr-1">— الحد الأقصى المسموح</span>
            </label>
            <input
              type="number"
              value={voltageDrop}
              onChange={(e) => setVoltageDrop(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm"
              placeholder="هبوط الجهد (%)"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              <i className="ri-percent-line ml-1 text-[#F5A623]"></i>
              معامل القدرة (PF)
              <span className="text-gray-400 font-normal mr-1">— نسبة القدرة الفعلية</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={powerFactor}
              onChange={(e) => {
                if (loadType !== 'resistive') setPowerFactor(e.target.value);
              }}
              readOnly={loadType === 'resistive'}
              className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm ${
                loadType === 'resistive' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="معامل القدرة"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <select
            value={wireMaterial}
            onChange={(e) => setWireMaterial(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm cursor-pointer"
          >
            {wireMaterials.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <select
            value={insulationType}
            onChange={(e) => setInsulationType(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5A623] text-sm cursor-pointer"
          >
            {insulationTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={calculateWireSize}
          className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-bold hover:bg-[#E09612] cursor-pointer whitespace-nowrap"
        >
          احسب
        </button>

        {errorMsg && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">{errorMsg}</div>
        )}

        {result && (
          <div className="bg-gradient-to-r from-[#F5A623]/10 to-[#3D3D3D]/10 rounded-lg p-4 border-r-4 border-[#F5A623] space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <i
                className={`text-lg ${
                  installationType === 'underground'
                    ? 'ri-arrow-down-line text-gray-600'
                    : 'ri-building-line text-[#F5A623]'
                }`}
              ></i>
              <span className="text-xs text-gray-500">
                {installationType === 'underground' ? 'تمديد تحت الأرض' : 'تمديد فوق الأرض'}
              </span>
            </div>
            <div className="bg-white/70 rounded-lg p-4 border-r-2 border-[#F5A623]">
              <p className="text-xs text-gray-500 mb-1">مقطع السلك المطلوب</p>
              <p className="text-3xl font-bold text-[#F5A623]">{result.wireSize} mm²</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'هبوط الجهد الفعلي', value: `${result.actualVoltageDrop} V` },
                { label: 'نسبة الهبوط', value: `${result.voltageDropPercent}%` },
                { label: 'المقاومة', value: `${result.resistance} Ω` },
                { label: 'القدرة المفقودة', value: `${result.powerLoss} W` },
              ].map((item) => (
                <div key={item.label} className="bg-white/70 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className="font-bold text-[#F5A623]">{item.value}</p>
                </div>
              ))}
            </div>
            {result.recommendation && (
              <div className="bg-amber-50 border-r-2 border-amber-500 rounded-lg p-3 flex gap-2">
                <i className="ri-lightbulb-line text-amber-600 mt-0.5"></i>
                <p className="text-sm text-amber-700">{result.recommendation}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CalculationsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('electrical');

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'electrical', label: 'الحسابات الكهربائية', icon: 'ri-calculator-line' },
    { id: 'wire', label: 'مقطع السلك', icon: 'ri-ruler-line' },
    { id: 'resistance', label: 'المقاومة', icon: 'ri-flashlight-line' },
  ];

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)' }}
      dir="rtl"
    >
      {/* Background decorative */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(#F5A623 1px, transparent 1px), linear-gradient(90deg, #F5A623 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(26,26,26,0.95)',
          borderBottom: '1px solid rgba(245,166,35,0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/645339dfad8e1015d6f60a0aacde7112/7ac018fe95b5893e2914eabc8075ecee.png"
                alt="Logo"
                className="h-12 w-12 object-contain"
              />
              <span className="font-bold text-white text-sm hidden sm:block">للتأسيسات والسيطرة الكهربائية QC</span>
            </Link>
            <nav className="flex items-center gap-2">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                <span>الرئيسية</span>
              </Link>
              <Link
                to="/contact"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white whitespace-nowrap cursor-pointer"
                style={{ background: '#F5A623', boxShadow: '0 2px 12px rgba(245,166,35,0.4)' }}
              >
                <i className="ri-customer-service-2-line"></i>
                <span>تواصل معنا</span>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero (الصورة الجديدة mur.png) */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/mur.png"
            alt="hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
        </div>
        <div className="relative z-10 w-full px-4 py-16 text-center text-white">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">الحسابات الكهربائية</h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl mx-auto">
            أدوات حساب التيار والقدرة وتحويل الوحدات وKVA ومقطع السلك والمقاومة
          </p>
        </div>
      </section>

      {/* Tab switcher */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 pt-6 w-full">
        <div
          className="flex gap-2 rounded-xl p-1.5"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap cursor-pointer ${
                activeTab === tab.id ? 'text-white shadow-md' : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              style={activeTab === tab.id ? { background: '#F5A623' } : {}}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="relative z-10 py-6 px-4 max-w-3xl mx-auto w-full flex-1">
        {activeTab === 'electrical' && <ElectricalCalculations />}
        {activeTab === 'wire' && <WireSizeCalculator />}
        {activeTab === 'resistance' && <ResistanceCalculator />}
      </main>

      <footer
        className="relative z-10 py-5 text-center mt-4"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
      >
        <p className="text-xs text-gray-500">
          تم البرمجة من قبل <span className="font-bold text-[#F5A623]">Murthdha abdulzahra</span>
        </p>
      </footer>
    </div>
  );
}